import { useProjects } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/StatusBadge';
import { Bell, Calendar, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDate, getPaymentStatus, getDaysUntilDue, isPaymentUpcoming } from '@/utils/helpers';

export const Reminders = () => {
  const { projects } = useProjects();
  const { user, isAdmin } = useAuth();

  // Filter projects based on user role
  const userProjects = isAdmin
    ? projects
    : projects.filter((p) => p.createdBy === user?.id);

  // Categorize payments
  const today = [];
  const thisWeek = [];
  const next30Days = [];
  const overdue = [];

  userProjects.forEach((project) => {
    project.paymentSchedule.forEach((payment) => {
      const status = getPaymentStatus(payment.dueDate, payment.status);
      if (status === 'paid') return;

      const daysUntil = getDaysUntilDue(payment.dueDate);
      const paymentWithProject = { ...payment, project };

      if (status === 'overdue') {
        overdue.push(paymentWithProject);
      } else if (daysUntil === 0) {
        today.push(paymentWithProject);
      } else if (daysUntil <= 7) {
        thisWeek.push(paymentWithProject);
      } else if (daysUntil <= 30) {
        next30Days.push(paymentWithProject);
      }
    });
  });

  const PaymentCard = ({ payment, highlight = false }) => {
    const status = getPaymentStatus(payment.dueDate, payment.status);
    const daysUntil = getDaysUntilDue(payment.dueDate);

    return (
      <Link to={`/projects/${payment.project.id}`}>
        <Card className={`card-hover ${highlight ? 'border-primary' : ''}`}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <p className="font-semibold truncate">{payment.paymentName}</p>
                  <StatusBadge status={status} />
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  {payment.project.projectName}
                </p>
                <p className="text-xs text-muted-foreground truncate mt-1">
                  Client: {payment.project.clientName}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Due: {formatDate(payment.dueDate)}
                  </span>
                  {status !== 'overdue' && (
                    <span className="text-xs font-medium text-primary">
                      ({daysUntil === 0 ? 'Today' : `${daysUntil} days`})
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-lg">{formatCurrency(payment.amount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  const EmptyState = ({ message }) => (
    <div className="text-center py-12">
      <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
      <p className="text-muted-foreground">{message}</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Payment Reminders</h1>
        <p className="text-muted-foreground mt-1">
          Stay on top of your upcoming and overdue payments
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={today.length > 0 ? 'border-primary' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Due Today</p>
                <p className="text-2xl font-bold mt-2">{today.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold mt-2">{thisWeek.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Next 30 Days</p>
                <p className="text-2xl font-bold mt-2">{next30Days.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={overdue.length > 0 ? 'border-destructive' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold mt-2 text-destructive">{overdue.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue="today" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="today" className="relative">
            Today
            {today.length > 0 && (
              <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {today.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="week">
            This Week
            {thisWeek.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {thisWeek.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="month">
            Next 30 Days
            {next30Days.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {next30Days.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overdue">
            Overdue
            {overdue.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {overdue.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-4">
          {today.length > 0 ? (
            today.map((payment) => <PaymentCard key={payment.id} payment={payment} highlight />)
          ) : (
            <Card>
              <CardContent>
                <EmptyState message="No payments due today" />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="week" className="space-y-4">
          {thisWeek.length > 0 ? (
            thisWeek.map((payment) => <PaymentCard key={payment.id} payment={payment} />)
          ) : (
            <Card>
              <CardContent>
                <EmptyState message="No payments due this week" />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="month" className="space-y-4">
          {next30Days.length > 0 ? (
            next30Days.map((payment) => <PaymentCard key={payment.id} payment={payment} />)
          ) : (
            <Card>
              <CardContent>
                <EmptyState message="No payments due in the next 30 days" />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="overdue" className="space-y-4">
          {overdue.length > 0 ? (
            <>
              <Card className="border-destructive/50 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Attention Required
                  </CardTitle>
                  <CardDescription>
                    These payments are overdue and need immediate action
                  </CardDescription>
                </CardHeader>
              </Card>
              {overdue.map((payment) => <PaymentCard key={payment.id} payment={payment} />)}
            </>
          ) : (
            <Card>
              <CardContent>
                <EmptyState message="No overdue payments - Great job!" />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};